import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  FolderOff as FolderOffIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { authGet, authPost, authDelete } from '../../services/apiService';

const ProjectNoCommonLibManagement = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ projectTarget: '' });

  // Fetch all projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authGet('/api/project-no-common-lib');
      
      if (response && response.success) {
        setProjects(response.projects || []);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.response?.data?.error || 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Handle open add dialog
  const handleOpenAddDialog = () => {
    setFormData({ projectTarget: '' });
    setDialogOpen(true);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ projectTarget: '' });
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.projectTarget.trim()) {
      setError('Project target is required');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      await authPost('/api/project-no-common-lib', formData);
      setSuccess('Project added successfully');
      handleCloseDialog();
      fetchProjects();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err.response?.data?.error || 'Failed to save project');
    }
  };

  // Handle delete
  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to remove this project from the exclusion list?')) {
      return;
    }

    try {
      setError(null);
      await authDelete(`/api/project-no-common-lib/${projectId}`);
      setSuccess('Project removed successfully');
      fetchProjects();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting project:', err);
      setError(err.response?.data?.error || 'Failed to delete project');
    }
  };

  // Clear messages
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  if (loading && projects.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <FolderOffIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Projects Without Common Libraries
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProjects}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Project
          </Button>
        </Box>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      {/* Projects Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Excluded Projects List
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Projects listed here will have their common library issues excluded from dashboard calculations.
            Format: project target name (e.g., highq_collaborate or tr/highq_collaborate).
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Project Target</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No projects found. Click "Add Project" to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  projects.map((project) => (
                    <TableRow key={project._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {project.projectTarget}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(project._id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Project Without Common Libraries</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Target"
                value={formData.projectTarget}
                onChange={(e) => setFormData({ ...formData, projectTarget: e.target.value })}
                placeholder="e.g., highq_collaborate or tr/highq_collaborate"
                helperText="Enter the project target name. Common library issues for this project will be excluded from calculations."
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectNoCommonLibManagement;

